import { useState } from 'react'
import { useCategories, useArchiveCategory, type Category } from '../lib/categories'
import EmptyState from '../components/EmptyState'
import CategoryForm from '../components/CategoryForm'

// Default categories that come pre-installed
const DEFAULT_CATEGORIES: Category[] = [
  { id: 'def-income-1', name: 'Salário', type: 'income', icon: 'briefcase', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-1', name: 'Alimentação', type: 'expense', icon: 'utensils', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-2', name: 'Transporte', type: 'expense', icon: 'car', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-3', name: 'Moradia', type: 'expense', icon: 'home', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-4', name: 'Contas', type: 'expense', icon: 'receipt', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-5', name: 'Saúde', type: 'expense', icon: 'heart', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-6', name: 'Educação', type: 'expense', icon: 'education', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-7', name: 'Investimentos', type: 'expense', icon: 'chart-line', color: null, is_essential: true, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
  { id: 'def-expense-8', name: 'Outros', type: 'expense', icon: 'gift', color: null, is_essential: false, is_active: true, created_at: '', updated_at: '', parent_category_id: null },
]

export default function CategoriesPage() {
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const { data: listResponse, isLoading, error } = useCategories()
  const archiveMutation = useArchiveCategory()

  // Use API data if available, otherwise use defaults
  const apiCategories = listResponse?.categories ?? []
  const isUsingDefaults = apiCategories.length === 0
  
  // Merge API categories with defaults (API categories take precedence)
  const allCategories = [...DEFAULT_CATEGORIES].map(defaultCat => {
    const apiCat = apiCategories.find(ac => ac.name.toLowerCase() === defaultCat.name.toLowerCase())
    return apiCat || defaultCat
  })
  
  // Add any API-only categories not in defaults
  const defaultNames = DEFAULT_CATEGORIES.map(c => c.name.toLowerCase())
  for (const apiCat of apiCategories) {
    if (!defaultNames.includes(apiCat.name.toLowerCase())) {
      allCategories.push(apiCat)
    }
  }


  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="inline-flex items-center gap-2 text-[var(--color-danger)]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>Erro ao carregar categorias</span>
        </div>
      </div>
    )
  }

  // Separate main categories from sub-categories
  const mainCategories = allCategories.filter(c => !c.parent_category_id)
  const subCategories = allCategories.filter(c => c.parent_category_id)

  // Get sub-categories for a parent
  const getSubCategories = (parentId: string) => {
    return subCategories.filter(sc => sc.parent_category_id === parentId)
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setIsFormOpen(true)
  }

  const handleDelete = (categoryId: string) => {
    archiveMutation.mutate(categoryId)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingCategory(null)
  }

  const handleNewCategory = () => {
    setEditingCategory(null)
    setIsFormOpen(true)
  }

  // Group categories by type for display
  const incomeMainCategories = mainCategories.filter(c => c.type === 'income')
  const expenseMainCategories = mainCategories.filter(c => c.type === 'expense')

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Categorias</h1>
        <button
          type="button"
          onClick={handleNewCategory}
          className="rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-brand-600)] transition-colors"
        >
          + Nova categoria
        </button>
      </div>

      {mainCategories.length === 0 && subCategories.length === 0 ? (
        <EmptyState
          icon={
            <svg xmlns="http://www.w3.org/24/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L5 14.14 2 9.27l5.91-1.01L12 2z" />
            </svg>
          }
          title="Nenhuma categoria cadastrada"
          description="Crie categorias para organizar suas transações de rendimento e despesa."
          action={{
            label: 'Criar categoria',
            onClick: handleNewCategory,
          }}
        />
      ) : (
        <div className="space-y-6">
          {/* Income Categories */}
          {incomeMainCategories.length > 0 && (
            <section>
              <h2 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 uppercase tracking-wide">
                Rendimentos
              </h2>
              <div className="space-y-3">
                {incomeMainCategories.map((category) => (
                  <div key={category.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{category.icon || '💰'}</span>
                        <div>
                          <p className="text-sm font-medium text-[var(--color-text)]">{category.name}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-xs rounded-full px-2 py-0.5 font-medium bg-green-100 text-green-600">
                              Rendimento
                            </span>
                            {category.color && (
                              <>
                                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color }} />
                                <span className="text-xs text-[var(--color-text-muted)]">Cor personalizada</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleEdit(category)}
                          className="rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)] transition-colors"
                          title="Editar"
                        >
                          <svg xmlns="http://www.w3.org/24/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M11.25 3.25l6.5 6.5H7.75L11.25 3.25z" />
                            <path fillRule="evenodd" d="M11.25 3.25v9.5M17.75 9.75H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(category.id)}
                          disabled={archiveMutation.isPending}
                          className="rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] transition-colors disabled:opacity-50"
                          title="Excluir"
                        >
                          <svg xmlns="http://www.w3.org/24/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                            <path fillRule="evenodd" d="M16.5 4.5V18M7.5 4.5v13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            <path d="M4.5 7.5h15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Expense Main Categories */}
          {expenseMainCategories.length > 0 && (
            <section>
              <h2 className="text-sm font-medium text-[var(--color-text-muted)] mb-3 uppercase tracking-wide">
                Despesas
              </h2>
              <div className="space-y-3">
                {expenseMainCategories.map((category) => {
                  const children = getSubCategories(category.id)
                  return (
                    <div key={category.id} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{category.icon || '📊'}</span>
                          <div>
                            <p className="text-sm font-medium text-[var(--color-text)]">{category.name}</p>
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-xs rounded-full px-2 py-0.5 font-medium bg-red-100 text-red-600">
                                Despesa
                              </span>
                              {category.color && (
                                <>
                                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: category.color }} />
                                  <span className="text-xs text-[var(--color-text-muted)]">Cor personalizada</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleEdit(category)}
                            className="rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)] transition-colors"
                            title="Editar"
                          >
                            <svg xmlns="http://www.w3.org/24/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.25 3.25l6.5 6.5H7.75L11.25 3.25z" />
                              <path fillRule="evenodd" d="M11.25 3.25v9.5M17.75 9.75H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(category.id)}
                            disabled={archiveMutation.isPending}
                            className="rounded-full p-1 text-[var(--color-text-muted)] hover:text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] transition-colors disabled:opacity-50"
                            title="Excluir"
                          >
                            <svg xmlns="http://www.w3.org/24/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                              <path fillRule="evenodd" d="M16.5 4.5V18M7.5 4.5v13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              <path d="M4.5 7.5h15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {/* Sub-categories */}
                      {children.length > 0 && (
                        <div className="mt-3 ml-8">
                          <h3 className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Subcategorias</h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {children.map((sub) => (
                              <div key={sub.id} className="bg-[var(--color-surface-subtle)] rounded-lg p-3 flex items-center gap-2 text-xs">
                                <span className="text-base">{sub.icon || '🔸'}</span>
                                <span className="text-[var(--color-text)]">{sub.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Category Form Modal */}
      <CategoryForm
        open={isFormOpen}
        onClose={handleCloseForm}
        category={editingCategory}
      />
    </div>
  )
}
